<template>
  <div>
    <div v-if="loading" class="tasks-loading">
      <div class="skeleton-task" v-for="i in 3" :key="i"></div>
    </div>

    <div v-else>
      <div class="tasks-section">
        <h2 class="tasks-group-title">{{$t('general_tasks')}}({{ generalTasks.length }})</h2>

        <div class="tasks-list">
          <div
              class="task-item"
              v-for="(task, index) in generalTasks"
              :key="task.taskId"
              :class="{'right-aligned': shouldAlignRight(index, generalTasks.length)}"
          >
            <div class="task-content">
              <div class="task-header">
                <div class="task-status">
                  <span class="status-dot" :class="{ 'active': task.isActive }"></span>
                  <span class="status-text">{{ task.isActive ?  $t('active_task') : $t('passive_task') }}</span>
                </div>
              </div>

              <h3 class="task-title">{{ task.name }}</h3>
              <p class="task-description">{{ task.description }}</p>

              <div class="task-actions">
                <div class="task-xp">
                  <div class="xp-badge">
                    <img src="@/assets/images/xp-coin.svg" alt="XP" class="xp-icon" />
                    <span>{{ task.xpPoints }} XP</span>
                  </div>
                </div>
                <div class="task-action-div">
                  <button class="edit-btn" @click="openEditModal(task)">
                    <img src="@/assets/images/editPen.svg" />
                  </button>
                </div>
              </div>
            </div>

            <div class="task-image">
              <img :src="task.iconUrl" alt="Task image" class="task-preview-image" />
            </div>
            <TaskDetailCard :task="task" :position="getCardPosition(index, generalTasks.length)" />
          </div>
        </div>
      </div>

      <div class="tasks-section" v-if="programTasks.length > 0 || true">
        <h2 class="tasks-group-title">{{$t('program_tasks')}} ({{ programTasks.length }})</h2>

        <div class="tasks-list">
          <div
              class="task-item"
              v-for="(task, index) in programTasks"
              :key="task.taskId"
              :class="{'right-aligned': shouldAlignRight(index, programTasks.length)}"
          >
            <div class="task-content">
              <div class="task-header">
                <div class="task-status">
                  <span class="status-dot" :class="{ 'active': task.isActive }"></span>
                  <span class="status-text">{{ task.isActive ? $t('active_task') : $t('passive_task') }}</span>
                </div>
              </div>

              <h3 class="task-title">{{ task.programInfo?.name || task.name }}</h3>
              <p class="task-description">{{ task.programInfo?.description || task.description }}</p>

              <div class="task-actions">
                <div class="task-xp">
                  <div class="xp-badge">
                    <img src="@/assets/images/xp-coin.svg" alt="XP" class="xp-icon" />
                    <span>{{ task.xpPoints }} XP</span>
                  </div>
                </div>
                <div class="task-action-div">
                  <button class="edit-btn" @click="openEditModal(task)">
                    <img src="@/assets/images/editPen.svg" />
                  </button>
                  <button class="delete-btn" @click="handleDeleteTask(task.taskId)">
                    <img src="@/assets/images/trash.svg" alt="Sil" />
                  </button>
                </div>
              </div>
            </div>

            <div class="task-image">
              <img :src="task.programInfo?.avatarUrl || task.iconUrl" alt="Task image" class="task-preview-image"/>
            </div>
          </div>

          <div class="add-task-box" @click="openAddModal('customProgram')">
            <div class="add-icon">+</div>
          </div>
        </div>
      </div>

      <div class="tasks-section" v-if="testTasks.length > 0 || true">
        <h2 class="tasks-group-title">{{$t('test_tasks')}} ({{ testTasks.length }})</h2>

        <div class="tasks-list">
          <div
              class="task-item"
              v-for="(task, index) in testTasks"
              :key="task.taskId"
              :class="{'right-aligned': shouldAlignRight(index, testTasks.length)}"
          >
            <div class="task-content">
              <div class="task-header">
                <div class="task-status">
                  <span class="status-dot" :class="{ 'active': task.isActive }"></span>
                  <span class="status-text">{{ task.isActive ? $t('active_task') : $t('passive_task') }}</span>
                </div>
              </div>

              <h3 class="task-title">{{ task.testInfo?.name || task.name }}</h3>
              <p class="task-description">{{ task.testInfo?.description || task.description }}</p>

              <div class="task-actions">
                <div class="task-xp">
                  <div class="xp-badge">
                    <img src="@/assets/images/xp-coin.svg" alt="XP" class="xp-icon" />
                    <span>{{ task.xpPoints }} XP</span>
                  </div>
                </div>
                <div class="task-action-div">
                  <button class="edit-btn" @click="openEditModal(task)">
                    <img src="@/assets/images/editPen.svg" alt="Düzenle" />
                  </button>
                  <button class="delete-btn" @click="handleDeleteTask(task.taskId)">
                    <img src="@/assets/images/trash.svg" alt="Sil" />
                  </button>
                </div>
              </div>
            </div>

            <div class="task-image">
              <img :src="task.testInfo?.avatarUrl || task.iconUrl" alt="Task image" class="task-preview-image" />
            </div>
          </div>

          <div class="add-task-box" @click="openAddModal('customTest')">
            <div class="add-icon">+</div>
          </div>
        </div>
      </div>

      <div class="tasks-section" v-if="visualTestTasks.length > 0 || true">
        <h2 class="tasks-group-title">{{$t('visual_test_tasks')}} ({{ visualTestTasks.length }})</h2>

        <div class="tasks-list">
          <div
              class="task-item"
              v-for="(task, index) in visualTestTasks"
              :key="task.taskId"
              :class="{'right-aligned': shouldAlignRight(index, visualTestTasks.length)}"
          >
            <div class="task-content">
              <div class="task-header">
                <div class="task-status">
                  <span class="status-dot" :class="{ 'active': task.isActive }"></span>
                  <span class="status-text">{{ task.isActive ? $t('active_task') : $t('passive_task') }}</span>
                </div>
              </div>

              <h3 class="task-title">{{ task.visualTestInfo?.name || task.name }}</h3>
              <p class="task-description">{{ task.visualTestInfo?.description || task.description }}</p>

              <div class="task-actions">
                <div class="task-xp">
                  <div class="xp-badge">
                    <img src="@/assets/images/xp-coin.svg" alt="XP" class="xp-icon" />
                    <span>{{ task.xpPoints }} XP</span>
                  </div>
                </div>
                <div class="task-action-div">
                  <button class="edit-btn" @click="openEditModal(task)">
                    <img src="@/assets/images/editPen.svg" alt="Düzenle" />
                  </button>
                  <button class="delete-btn" @click="handleDeleteTask(task.taskId)">
                    <img src="@/assets/images/trash.svg" alt="Sil" />
                  </button>
                </div>
              </div>
            </div>

            <div class="task-image">
              <img :src="task.visualTestInfo?.avatarUrl || task.iconUrl" alt="Task image" class="task-preview-image" />
            </div>
          </div>

          <div class="add-task-box" @click="openAddModal('customVisualTest')">
            <div class="add-icon">+</div>
          </div>
        </div>
      </div>
    </div>

    <TaskEditModal
      :visible="isEditModalVisible"
      :task="selectedTask"
      :on-hide="closeEditModal"
      :on-success="handleTaskUpdate"
      :on-delete="() => $emit('delete', selectedTask.taskId)"
    />

    <AddTaskModal
      :visible="isAddModalVisible"
      :task-type="addTaskType"
      :task-id="selectedTaskId"
      :on-hide="closeAddModal"
      :on-success="handleTaskAdd"
    />
  </div>
</template>

<script>
import TaskDetailCard from "@/components/gamification/TaskDetailCard.vue";
import TaskEditModal from "@/components/gamification/TaskEditModal.vue";
import AddTaskModal from "@/components/gamification/AddTaskModal.vue";
import gamificationService from '@/service/gamificationService';

export default {
  name: "GamificationTasks",
  components: {
    TaskDetailCard,
    TaskEditModal,
    AddTaskModal
  },
  props: {
    tasks: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    updateData:{
      type: Function,
      default: ()=>{},
    }
  },
  data() {
    return {
      isEditModalVisible: false,
      selectedTask: null,
      isAddModalVisible: false,
      addTaskType: 'customProgram',
      selectedTaskId: null
    };
  },
  computed: {
    generalTasks() {
      return this.tasks.filter(task => task.taskType === 'general');
    },
    programTasks() {
      return this.tasks.filter(task => task.taskType === 'customProgram');
    },
    testTasks() {
      return this.tasks.filter(task => task.taskType === 'customTest');
    },
    visualTestTasks() {
      return this.tasks.filter(task => task.taskType === 'customVisualTest');
    },
    programTaskId() {
      const programTask = this.tasks.find(task =>
        task.taskType === 'general' && task.taskIdentifier === 'program'
      );
      return programTask?.originalTaskId;
    },
    testTaskId() {
      const testTask = this.tasks.find(task =>
        task.taskType === 'general' && task.taskIdentifier === 'test'
      );
      return testTask?.originalTaskId;
    },
    visualTestTaskId() {
      const visualTestTask = this.tasks.find(task =>
        task.taskType === 'general' && task.taskIdentifier === 'gameLabs'
      );
      return visualTestTask?.originalTaskId;
    }
  },
  emits: ['edit', 'delete', 'add', 'update', 'add-success'],
  methods: {
    shouldAlignRight(index, total) {
      const columnsPerRow = this.getColumnsPerRow();
      return (index + 1) % columnsPerRow === 0;
    },
    getCardPosition(index, total) {
      const columnsPerRow = this.getColumnsPerRow();
      return (index + 1) % columnsPerRow === 0 ? 'left' : 'right';
    },
    getColumnsPerRow() {
      if (window.innerWidth >= 768) {
        return 2;
      } else {
        return 1;
      }
    },
    openEditModal(task) {
      this.selectedTask = { ...task };
      this.isEditModalVisible = true;
    },
    closeEditModal() {
      this.isEditModalVisible = false;
    },
    handleTaskUpdate(updatedTask) {
      this.$emit('update', updatedTask);
      this.closeEditModal();
      this.updateData();
    },
    openAddModal(taskType) {
      this.addTaskType = taskType;
      if (taskType === 'customProgram') {
        this.selectedTaskId = this.programTaskId;
      } else if (taskType === 'customVisualTest') {
        this.selectedTaskId = this.visualTestTaskId;
      } else {
        this.selectedTaskId = this.testTaskId;
      }
      this.isAddModalVisible = true;
    },
    closeAddModal() {
      this.isAddModalVisible = false;
    },
    handleTaskAdd(newTask) {
      this.$emit('add-success', newTask);
      this.updateData();

    },
    async handleDeleteTask(taskId) {
      try {
        const response = await gamificationService.deleteCustomGamificationTask(taskId);

        if (response?.data?.responseType === 'success') {

          this.$snackbar.add({
            text: response.data.message || this.$t('task_deleted_successfully'),
            type: 'success'
          });
        }
        this.updateData();
      } catch (error) {
        this.$snackbar.add({
          text: this.$t('task_delete_error'),
          type: 'error'
        });
      }
    }
  },
  mounted() {
    this.$watch('tasks', (val) => {
      if (val && val.length > 0) {
        console.log('[DEBUG] General tasks:', val.filter(t => t.taskType === 'general').map(t => ({ taskId: t.taskId, taskIdentifier: t.taskIdentifier })));
      }
    }, { immediate: true });
    window.addEventListener('resize', () => this.$forceUpdate());
  },
  beforeUnmount() {
    window.removeEventListener('resize', () => this.$forceUpdate());
  }
}
</script>

<style scoped>
.tasks-loading {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.skeleton-task {
  height: 150px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 12px;
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.tasks-section {
  margin-bottom: 40px;
  z-index: 1 !important;
}

.tasks-group-title {
  font-size: 20px;
  font-weight: 600;
  color: #040C2D;
  margin-bottom: 20px;
}

.tasks-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  z-index: 30 !important;
}

@media (min-width: 768px) {
  .tasks-list {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1200px) {
  .tasks-list {
    grid-template-columns: repeat(2, 1fr);
  }
}

.task-item {
  background-color: #FFFFFF;
  border-radius: 12px;
  border: 1px solid #EAECF0;
  display: flex;
  align-items: center;
  position: relative;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  isolation: isolate;
  opacity: 1;
  justify-content: center;
}

.task-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08);
  z-index: 100 !important;
}

.task-content {
  flex: 1;
  padding: 24px;
  display: flex;
  flex-direction: column;
}

.task-image {
  width: 170px;
  max-width: 170px;
  background-color: #F2F4F7;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-right: 20px;
  padding: 10px 14px;
  border-radius: 8px;
  height: auto;

}

.task-preview-image {
  max-width: 120px;
  max-height: 160px;
  object-fit: cover;
  border-radius: 8px;
  align-items: center;
  justify-content: center;
}

/* Yeni Ekle Kutusu Stilleri */
.add-task-box {
  border: 2px dashed #D0D5DD;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  background-color: #F9FAFB;
  cursor: pointer;
  transition: all 0.3s ease;
}

.add-task-box:hover {
  background-color: #F5F7FA;
  transform: translateY(-5px);
  border-color: #98A2B3;
}

.add-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 50px;
  color: #667085;
  transition: all 0.3s ease;
}

.add-task-box:hover .add-icon {
  color: #344054;
}

.add-task-box:hover .add-text {
  color: #344054;
}

.task-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #98A2B3;
  display: inline-block;
  margin-right: 8px;
}

.status-dot.active {
  background-color: #12B76A;
}

.status-text {
  font-size: 14px;
  color: #475467;
  font-weight: 500;
}


.xp-badge {
  display: flex;
  align-items: center;
  background-color: #FCF5E8;
  border-radius: 16px;
  padding: 4px 12px;
}

.xp-icon {
  width: 20px;
  height: 20px;
  margin-right: 4px;
}

.task-title {
  font-size: 20px;
  font-weight: 600;
  color: #101828;
  margin-bottom: 8px;
  line-height: 28px;
}

.task-description {
  font-size: 14px;
  color: #475467;
  line-height: 20px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex-grow: 1;
}

.task-actions {
  display: flex;
  gap: 12px;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  margin-top: 17px;
}

.edit-btn, .delete-btn {
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s;
}

.edit-btn:hover {
  background-color: #F2F4F7;
}

.delete-btn:hover {
  background-color: #FEF3F2;
}

.task-action-div{
  display: flex;
  gap: 8px;
}

@media (max-width: 767px) {
  .task-item {
    flex-direction: column;
  }

  .task-image {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
    height: 180px;
    order: -1;
  }
}
</style>

